import {
  defineComponent,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
} from 'vue';
import type { DefineComponent, PropType } from 'vue';

import {
  getOrCreateWorkerPoolSingleton,
  terminateWorkerPoolSingleton,
  type WorkerInitializationRenderOptions,
  type WorkerPoolManager,
  type WorkerPoolOptions,
} from '../worker';
import { WorkerPoolInjectionKey } from './injection';

export interface WorkerPoolProviderPropsVue {
  poolOptions: WorkerPoolOptions;
  highlighterOptions: WorkerInitializationRenderOptions;
}

// Tracks how many providers are currently mounted so the shared worker-pool
// singleton is only terminated once the last one unmounts (debounces rapid
// mount/unmount churn). Mirrors the React `WorkerPoolContextProvider`.
let mountedProviderCount = 0;

const WorkerPoolProviderImpl = defineComponent({
  name: 'PierreWorkerPoolProvider',
  props: {
    poolOptions: {
      type: Object as PropType<WorkerPoolOptions>,
      required: true,
    },
    highlighterOptions: {
      type: Object as PropType<WorkerInitializationRenderOptions>,
      required: true,
    },
  },
  setup(props, { slots }) {
    // Create (or reuse) the singleton during setup so child components can
    // inject it on their first render. No-op on the server.
    const poolManager: WorkerPoolManager | undefined =
      typeof window === 'undefined'
        ? undefined
        : getOrCreateWorkerPoolSingleton({
            poolOptions: props.poolOptions,
            highlighterOptions: props.highlighterOptions,
          });
    provide(WorkerPoolInjectionKey, poolManager);

    onMounted(() => {
      if (poolManager != null) {
        mountedProviderCount++;
      }
    });
    onBeforeUnmount(() => {
      if (poolManager != null) {
        mountedProviderCount--;
        if (mountedProviderCount === 0) {
          terminateWorkerPoolSingleton();
        }
      }
    });

    return () => slots.default?.();
  },
});

export const WorkerPoolProvider =
  WorkerPoolProviderImpl as unknown as DefineComponent<WorkerPoolProviderPropsVue>;

// Inject the shared worker pool provided by a `WorkerPoolProvider` ancestor.
// Returns undefined when there is no provider (or on the server).
export function useWorkerPool(): WorkerPoolManager | undefined {
  return inject(WorkerPoolInjectionKey, undefined);
}
